import env from '@config/env';
import encryptedStorage from '@storage/encryptedStorage';
import { STORAGE_KEYS } from '@storage/storageKeys';
import { getCookie } from '@utils/cookie';

/**
 * Unwrap `{ success, data: { ... } }` auth envelopes (verify OTP, login).
 */
export const unwrapAuthData = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return {};
  const inner = payload.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner;
  }
  return payload;
};

const pickTokenField = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return null;
  for (const key of keys) {
    const value = obj[key];
    if (value != null && String(value).trim()) return String(value);
  }
  return null;
};

/**
 * Normalize auth token fields from API payloads (camelCase / snake_case / aliases).
 * Checks both the unwrapped `data` object and the top-level payload so tokens are
 * not dropped when the user object is nested under `data`.
 */
export const extractAuthTokens = (payload = {}) => {
  const data = unwrapAuthData(payload);
  const accessKeys = ['accessToken', 'token', 'access_token', 'jwt'];
  const refreshKeys = ['refreshToken', 'refresh_token', 'refresh'];

  const accessToken = pickTokenField(data, accessKeys) || pickTokenField(payload, accessKeys);
  const refreshToken = pickTokenField(data, refreshKeys) || pickTokenField(payload, refreshKeys);
  const expiresIn =
    data?.expiresIn ?? data?.expires_in ?? payload?.expiresIn ?? payload?.expires_in ?? null;

  return {
    accessToken: accessToken ? String(accessToken) : null,
    refreshToken: refreshToken ? String(refreshToken) : null,
    expiresIn,
    data,
  };
};

/**
 * Decode a JWT payload without verifying the signature.
 * Used to read claims such as userId for AES key derivation.
 */
export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/** Reads `mustChangePassword` from login payload and/or access-token claims. */
export const resolveMustChangePassword = (payload = {}, token) => {
  const { accessToken, data } = extractAuthTokens(payload);
  if (data?.mustChangePassword != null) return Boolean(data.mustChangePassword);

  const claims = decodeJwtPayload(token || accessToken || getAccessToken());
  return Boolean(claims?.mustChangePassword);
};

const normalizeToken = (value) => {
  if (value == null) return null;
  let token = String(value).trim();
  if (!token || token === 'undefined' || token === 'null') return null;
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    token = token.slice(1, -1).trim();
  }
  if (/^bearer\s+/i.test(token)) {
    token = token.replace(/^bearer\s+/i, '').trim();
  }
  if (!token || token === 'undefined' || token === 'null') return null;
  return token;
};

/**
 * Access token for Authorization.
 * Uses TOKEN_KEY from the loaded portal env (.env.admin → ds_admin_token,
 * .env.merchant → ds_merchant_token).
 *
 * Always re-read localStorage on each call so a token edited in Application
 * is sent on the next API request. Cookie is only used when the storage key
 * is missing (page-refresh restore). A leftover cookie must not win over a
 * value that is already in localStorage.
 */
export const getAccessToken = () => {
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(env.TOKEN_KEY);
      if (raw != null && raw !== '') {
        const fromStorage = normalizeToken(encryptedStorage.getItem(env.TOKEN_KEY));
        if (fromStorage) return fromStorage;
        // Key is present — do not attach a stale cookie token.
        return null;
      }
    } catch {
      // Private mode / blocked storage — fall through to cookie.
    }
  }

  return normalizeToken(getCookie(env.TOKEN_KEY));
};

/** True when there is no access token or its JWT `exp` has passed. */
export const isAccessTokenExpired = (token = getAccessToken()) => {
  const normalized = normalizeToken(token);
  if (!normalized) return true;

  const claims = decodeJwtPayload(normalized);
  if (!claims || typeof claims.exp !== 'number') return false;

  return claims.exp * 1000 <= Date.now() + 5000;
};

const clearAuthorizationHeader = (headers) => {
  if (!headers) return headers;

  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
  }

  delete headers.Authorization;
  delete headers.authorization;

  if (headers.common && typeof headers.common === 'object') {
    delete headers.common.Authorization;
    delete headers.common.authorization;
  }

  return headers;
};

/** UUID from a JWT payload (userId / user_id / sub when it looks like a UUID). */
export const getUserIdFromToken = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload !== 'object') return null;

  const candidate = payload.userId || payload.user_id || payload.sub || null;
  if (candidate == null || candidate === '') return null;

  return String(candidate);
};

export const setAuthorizationHeader = (headers, token) => {
  if (!headers || !token) return headers;

  const value = `Bearer ${token}`;
  if (typeof headers.set === 'function') {
    headers.set('Authorization', value);
  }
  headers.Authorization = value;
  return headers;
};

/** Attach the current storage token (admin or merchant TOKEN_KEY) to request headers. */
export const applyAuthorizationHeader = (headers) => {
  const nextHeaders = headers || {};
  clearAuthorizationHeader(nextHeaders);
  const token = getAccessToken();
  if (!token) return nextHeaders;
  return setAuthorizationHeader(nextHeaders, token);
};

/** Shared refresh-token path. Called on 401 for admin and merchant. */
export const getRefreshTokenPath = () => '/auth/refresh';

/** Refresh token from storage / cookie / last OTP payload so a 401 can call the refresh API. */
export const getRefreshToken = () => {
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(env.REFRESH_TOKEN_KEY);
      if (raw != null && raw !== '') {
        const fromStorage = normalizeToken(encryptedStorage.getItem(env.REFRESH_TOKEN_KEY));
        if (fromStorage) return fromStorage;
        return null;
      }
    } catch {
      // ignore
    }
  }

  const fromCookie = normalizeToken(getCookie(env.REFRESH_TOKEN_KEY));
  if (fromCookie) return fromCookie;

  const verifyResponse = encryptedStorage.getItem(STORAGE_KEYS.VERIFY_OTP_RESPONSE);
  if (verifyResponse && typeof verifyResponse === 'object') {
    const fromVerify = normalizeToken(extractAuthTokens(verifyResponse).refreshToken);
    if (fromVerify) return fromVerify;
  }

  return null;
};

const toUrlPath = (url = '') => {
  const raw = String(url || '').split('?')[0];
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname;
    } catch {
      return raw;
    }
  }
  return raw;
};

/** Endpoints that must not trigger a refresh-token cycle on 401. */
export const shouldSkipTokenRefresh = (url = '') => {
  const path = toUrlPath(url);

  return (
    /\/auth\/(login|logout|refresh|forgot-password|reset-password)\/?$/.test(path) ||
    /\/admin\/auth\/(login|logout|refresh|change-password)\/?$/.test(path) ||
    /\/merchant\/auth\/otp\/(send|verify|resend)\/?$/.test(path) ||
    /\/merchant\/account\/contact\/otp\/(send|verify)\/?$/.test(path) ||
    /\/chotuCore\/pa\/v1\/create-link\/?$/.test(path)
  );
};

export default extractAuthTokens;
